import React from 'react';
import i18next from 'i18next';
import { connect } from 'react-redux';
import classNames from 'classnames';
import get from 'lodash.get';
import getConfig from 'next/config';

import { isShellArticle as isShell } from 'lib/article';
import { isTelemundoVertical } from 'lib/vertical';
import { TVE_CANONICAL_ROUTE } from 'lib/tve';
import { usePrevious } from 'lib/Hooks/usePrevious';
import BTE from 'lib/BTE';
import { isElectionsTaxonomy } from 'lib/taxonomy';
import { useShouldRemoveAds } from 'lib/subscriptionUtils';

import Ad from 'components/Ad';
import { ErrorBoundary } from 'components/ErrorBoundary';
import { AlertBanner } from 'components/AlertBanner';
import { Marquee } from 'components/Marquee';
import ProgramGuide from 'components/ProgramGuide';
import { QuickLinks } from 'components/QuickLinks';

import { ServiceHeader } from '../Header';
import { ServiceFooter } from '../Footer';

import './styles.themed.scss';

/**
 *
 * @param {object} root0
 * @param {Article} root0.article
 * @param {object} root0.router
 * @param {object} root0.shared
 * @param {object} root0.front
 * @param {object} root0.recipe
 * @param {object} root1
 * @param {VerticalType} root1.vertical
 */
const mapStateToProps = ({
  article,
  router,
  shared,
  front,
  recipe,
}, { vertical }) => {
  const adsEnabled = get(article, ['content', 0, 'adsEnabled'], true);
  const ecommerceEnabled = get(article, ['content', 0, 'ecommerceEnabled'], false)
    || get(recipe, ['current', 'ecommerceEnabled'], false);
  const nativeAd = get(article, 'isNativeAd', false) || get(recipe, ['current', 'nativeAd'], false);
  const adsDisabledByCurator = get(front, 'curation.adsDisabled', false);
  const hasElections = isElectionsTaxonomy(article?.content?.[0]?.taxonomy?.primaryTopic?.slug);

  return {
    adsEnabled: adsEnabled && !ecommerceEnabled && !nativeAd,
    isShellArticle: isShell(article),
    currentPath: router.path,
    pageView: shared.pageView,
    vertical: shared.vertical || vertical,
    adsDisabledByCurator,
    isEmbedProgramGuide: front.isEmbedProgramGuide,
    isElectionsNavEnabled: hasElections,
  };
};

/**
 * Determines whether the banner ad should be displayed based on various conditions.
 *
 * @param {object} params - The parameters for determining banner ad visibility.
 * @param {boolean} params.adsDisabledByCurator - Whether ads are disabled by the curator.
 * @param {boolean} params.adsDisabledByParentComponent - Whether ads are disabled by the parent component.
 * @param {boolean} params.adsEnabled - Whether ads are enabled globally.
 * @param {string} params.currentPath - The current URL path.
 * @param {string} params.pageRoute - The route of the current page.
 * @param {string} params.pageView - The type of the current page view (e.g., 'article', 'front').
 * @param {string} params.vertical - The vertical/category of the current page (e.g., 'msnbc', 'today').
 * @param {boolean} params.forceHideBannerAd - Whether the banner ad is forcibly hidden.
 * @param {boolean} params.shouldRemoveAds - Whether ads should be hidden for the user.
 */
function getShouldShowBannerAd({
  adsDisabledByCurator,
  adsDisabledByParentComponent,
  adsEnabled,
  currentPath,
  pageRoute,
  pageView,
  vertical,
  forceHideBannerAd,
  shouldRemoveAds,
}) {
  if (adsDisabledByParentComponent) {
    return false;
  }

  if (shouldRemoveAds) {
    return false;
  }
  // Blanket disable for video and slideshow pages
  if (forceHideBannerAd || ['video', 'slideshow'].includes(pageView)) {
    return false;
  }
  // Disable on MSNBC TVE page
  if (vertical === 'msnbc' && currentPath.includes(`/${TVE_CANONICAL_ROUTE}`)) {
    return false;
  }

  // Disable on TODAY rebrand fronts
  if (vertical === 'today' && pageRoute === '/') {
    return false;
  }

  // [1] If a vertical has no key in `disableAd`, the bannerAd will be enabled for all pageViews
  // [2] An entry of `all` will disable the bannerAd on all pageViews for the given vertical
  // Examples:
  // Disable on Mach fronts and slideshows -- mach: ['front', 'slideshow']
  // Disable on all Mach page types -- mach: ['all']
  const disableAd = {
  };

  const disabledOnPages = disableAd[vertical];

  const isArticleWithAdsEnabled = pageView === 'article' && adsEnabled;
  const isAdsEnabledByCurator = !adsDisabledByCurator;
  const isFrontAndAdsEnabledByCurator = pageView === 'front' && isAdsEnabledByCurator;
  // recipes will always have ads enabled
  const isRecipeWithAdsEnabled = pageView === 'recipe' && adsEnabled;
  const showBannerAd = isArticleWithAdsEnabled
    || isFrontAndAdsEnabledByCurator
    || isRecipeWithAdsEnabled;

  // [1] For verticals without keys in `disableAd`
  if (typeof disabledOnPages === 'undefined' && showBannerAd) {
    return true;
  }

  // [2] For verticals marked `all` or with specific page types
  if (disabledOnPages && (disabledOnPages.includes('all') || disabledOnPages.includes(pageView))) {
    return false;
  }

  return showBannerAd;
}

/**
 * HeaderAndFooter component that renders the header and footer along with ads and other elements.
 *
 * @param {object} props - The component props.
 * @param {string} props.currentPath - The current URL path.
 * @param {string} props.vertical - The vertical/category of the current page (e.g., 'news', 'sports').
 * @param {boolean} props.adsDisabledByCurator - Whether ads are disabled by the curator.
 * @param {boolean} props.adsDisabledByParentComponent - Whether ads are disabled by the parent component.
 * @param {boolean} props.adsEnabled - Whether ads are enabled globally.
 * @param {React.ReactNode} props.children - The child elements to render within the layout.
 * @param {string} props.pageRoute - The route of the current page.
 * @param {boolean} props.isEmbedProgramGuide - Whether the program guide is embedded.
 * @param {boolean} props.isShellArticle - Whether the page is a shell article.
 * @param {React.ReactNode} props.contentAboveHeader - Content to render above the header.
 * @param {object} props.layout - The layout configuration for the header, footer, and marquees.
 * @param {object} props.pageView - The type of the current page view (e.g., 'article', 'front').
 * @param {boolean} props.isNavbarSticky - Whether the navbar is sticky.
 * @param {boolean} props.isElectionsNavEnabled - Whether the elections navigation is enabled.
 * @param {boolean} props.onlyRenderMarquee - Whether to render only the marquee.
 * @param {React.ReactNode} props.contentBelowHeader - Content to render below the header.
 * @param {import('lib/types/subnav').SubnavResponseItem} props.quickLinksSubnav - Quick Links items.  Pass an empty array if not applicable.
 * @param {'visible' | 'disabled' | 'collapseOnScroll'} props.mobileNavInsertVisibility - The visibility state of the mobile navigation insert.
 */
function HeaderAndFooter({
  currentPath,
  vertical,
  adsDisabledByCurator = false,
  adsDisabledByParentComponent = false,
  adsEnabled = true,
  children = null,
  pageRoute = '',
  isEmbedProgramGuide = false,
  isShellArticle = false,
  contentAboveHeader = null,
  layout = {
    header: {},
    footer: {},
    marquees: [],
  },
  pageView = null,
  isNavbarSticky = true,
  isElectionsNavEnabled = false,
  onlyRenderMarquee = false,
  contentBelowHeader,
  mobileNavInsertVisibility = 'visible',
  quickLinksSubnav,
}) {
  const { publicRuntimeConfig: { TOP_BANNER_AD_REFRESH_INTERVAL } } = getConfig();

  const refreshIntervalConfigValue = parseInt(TOP_BANNER_AD_REFRESH_INTERVAL, 10);
  const bannerAdRefreshInterval = !Number.isNaN(refreshIntervalConfigValue)
    ? refreshIntervalConfigValue
    // using `undefined` here to allow the ad to fall back to the default refresh
    // interval set globally in `getEnigmaConfig`
    : undefined;

  const [isTopBannerAdUnstuck, setIsTopBannerAdUnstuck] = React.useState(false);
  const [isAdInitialRenderComplete, setIsAdInitialRenderComplete] = React.useState(false);
  const [isScrolledToTop, setIsScrolledToTop] = React.useState(true);
  const [timeLeftForStickyAd, setTimeLeftForStickyAd] = React.useState('0s');

  // using a ref here for `isAdRendered` rather than the state `isAdInitialRenderComplete`
  // declared above, in order to reference it within a timeout set in useEffect below
  const isAdRendered = React.useRef(false);
  const adRenderTimeoutId = React.useRef();
  const adStickyTimeoutId = React.useRef();

  // Ad-free experience check for users with subscription entitlement
  const shouldRemoveAds = useShouldRemoveAds();
  const mpsAd = React.useRef();

  const isNewsLandingPage = vertical === 'news' && pageRoute === '/';
  const AD_RENDER_TIMEOUT = isNewsLandingPage ? 6 * 1000 : 3 * 1000;
  const AD_STICKY_TIMEOUT = isNewsLandingPage ? 5 * 1000 : 4 * 1000;
  let timerEnd;
  let timesScrolledToTop = 0;
  const previousPageView = usePrevious(pageView);

  // I don't think the below is needed since we no longer "transport" from video/slideshow->fronts
  // but leaving it here for now.
  // When transporting from a video or slideshow to a front, force the banner ad to be hidden
  const forceHideBannerAd = ['video', 'slideshow'].includes(previousPageView)
    && pageView === 'front';

  React.useEffect(() => {
    // when component first mounts, wait maximum of AD_RENDER_TIMEOUT time,
    // then if the ad render event hasn't fired by then, go ahead and un-stick
    // the banner ad
    adRenderTimeoutId.current = setTimeout(() => {
      if (!isAdRendered.current) {
        // if the ad request hasn't been initiated by now, something is probably
        // wrong. go ahead and unstick so we don't have a permanently `sticky` ad
        setIsTopBannerAdUnstuck(true);
      }
    }, AD_RENDER_TIMEOUT);

    return () => {
      clearTimeout(adRenderTimeoutId.current);
      // clear the timeout for the sticky timeout on unmount as well, just in case
      // we are unmounting before that completes
      clearTimeout(adStickyTimeoutId.current);
    };
  }, []);

  /**
   *
   */
  const getTimeLeftForStickyTimeout = () => {
    const secondsToEnd = Math.ceil((timerEnd - new Date().getTime()) / 1000 - 1.2);
    return !Number.isNaN(secondsToEnd) && secondsToEnd > 0 ? `${secondsToEnd}s` : '0s';
  };

  const bannerAdHandler = React.useCallback((handlerAd) => {
    mpsAd.current = mpsAd.current || handlerAd;
    mpsAd.current.onRender(() => {
      isAdRendered.current = true;
      setIsAdInitialRenderComplete(true);
      setIsTopBannerAdUnstuck(false);

      // when the ad first renders, clear the adRenderTimeoutId timeout, and show
      // the ad (keep it sticky) for another AD_STICKY_TIMEOUT amount of time
      clearTimeout(adRenderTimeoutId.current);
      adStickyTimeoutId.current = setTimeout(() => {
        setIsTopBannerAdUnstuck(true);
      }, AD_STICKY_TIMEOUT);
      timerEnd = new Date().getTime() + AD_STICKY_TIMEOUT;
    });
  }, []);

  const shouldRenderMarquee = !contentAboveHeader && !isShellArticle;

  /**
   * renders Marquee
   */
  const getMarquee = () => (
    shouldRenderMarquee && (
      <ErrorBoundary>
        <Marquee marquees={layout.marquees} isNewsLandingPage={isNewsLandingPage} />
      </ErrorBoundary>
    )
  );

  /**
   *
   */
  const getTopBar = () => {
    if (contentAboveHeader) {
      return (
        <ErrorBoundary>
          {contentAboveHeader}
        </ErrorBoundary>
      );
    }

    return null;
  };

  React.useEffect(() => {
    /**
     *
     * @param {number} scrollDepth
     */
    function handleScroll(scrollDepth) {
      const scrollDepthThreshold = layout?.marquees?.length > 0 ? 165 : 0;
      if (scrollDepth <= scrollDepthThreshold) {
        setIsScrolledToTop(true);
        if (isAdInitialRenderComplete) {
          timesScrolledToTop += 1;
        }
      } else {
        setIsScrolledToTop(false);
      }

      if (timesScrolledToTop > 0) {
        setTimeLeftForStickyAd(getTimeLeftForStickyTimeout());
      }
    }

    handleScroll(window.scrollY);
    BTE.on('scroll', handleScroll, 'debounce');

    return () => {
      BTE.remove('scroll', handleScroll, 'debounce');
    };
  }, [BTE, isAdInitialRenderComplete]);

  const shouldShowBannerAd = getShouldShowBannerAd({
    adsDisabledByCurator,
    adsDisabledByParentComponent,
    adsEnabled,
    currentPath,
    pageView,
    vertical,
    forceHideBannerAd,
    pageRoute,
    shouldRemoveAds,
  });

  const bannerAdClasses = classNames(
    'header-and-footer--banner-ad',
    'ad-container topbannerAd',
    {
      'landing-header-footer-banner-ad': isNewsLandingPage,
      'header-and-footer--static': isTopBannerAdUnstuck,
      'landing-slide-animation': isNewsLandingPage && !isTopBannerAdUnstuck,
      'animation-delay': isNewsLandingPage && isAdInitialRenderComplete,
      'hide-animation': isNewsLandingPage && isScrolledToTop && isAdInitialRenderComplete,
      'landing-animation-scrolled': isNewsLandingPage && !isScrolledToTop && isAdInitialRenderComplete,
      // The header footer service listens for a class change
      // to recalc its position. The below 👇 classes serve to trigger that
      isAdInitialRenderComplete,
      isScrolledToTop,
    },
  );

  // Program Guide Excluded Paths
  const TELEMUNDO_EXCLUDED_PATHS = [
    '/noticias/noticias-telemundo-ahora',
    '/deportes/telemundo-deportes-ahora',
    '/telemundo-al-dia',
    '/canales-de-streaming-en-vivo',
  ];

  /**
   * Checks if the Telemundo Program Guide should be visible based on the current path.
   * @param {string} path - The current URL path.
   */
  function isTelemundoProgramGuideVisible(path) {
    return !TELEMUNDO_EXCLUDED_PATHS.includes(path);
  }

  /**
   *
   */
  const getHeaderLayout = () => (isNewsLandingPage ? (
    <>
      {getMarquee()}
      <div className="navs-container">
        <ErrorBoundary>
          {/* Alert for IE11 */}
          <AlertBanner alertText={i18next.t('IE11 ALERT')} />
        </ErrorBoundary>

        <ErrorBoundary>
          {getTopBar()}
        </ErrorBoundary>

        <ErrorBoundary>
          <ServiceHeader
            adsEnabled={adsEnabled}
            currentPath={currentPath}
            header={layout.header}
            pageView={pageView}
            vertical={vertical}
            isNavbarSticky={isNavbarSticky}
            quickLinksSubnav={quickLinksSubnav}
          />
        </ErrorBoundary>
      </div>
      {shouldShowBannerAd && (
        <ErrorBoundary>
          <div
            className={bannerAdClasses}
            style={{
              ...(timeLeftForStickyAd !== '0s' && { animationDelay: timeLeftForStickyAd }),
            }}
          >
            <Ad
              handler={bannerAdHandler}
              slot="topbanner"
              refreshInterval={bannerAdRefreshInterval}
            />
            <p className="ad-placeholder">Advertisement</p>
          </div>
        </ErrorBoundary>
      )}
    </>
  ) : (
    <>
      <ErrorBoundary>
        {shouldShowBannerAd && (
          <div className={bannerAdClasses}>
            <Ad
              handler={bannerAdHandler}
              slot="topbanner"
              refreshInterval={bannerAdRefreshInterval}
            />
          </div>
        )}
      </ErrorBoundary>
      {getMarquee()}
      <div className="navs-container">
        <ErrorBoundary>
          {/* Alert for IE11 */}
          <AlertBanner alertText={i18next.t('IE11 ALERT')} />
        </ErrorBoundary>

        {getTopBar()}

        <ErrorBoundary>
          <ServiceHeader
            adsEnabled={adsEnabled}
            currentPath={currentPath}
            header={layout.header}
            pageView={pageView}
            vertical={vertical}
            isNavbarSticky={isNavbarSticky}
            isElectionsNavEnabled={isElectionsNavEnabled}
            mobileNavInsertVisibility={mobileNavInsertVisibility}
          />
        </ErrorBoundary>
        {contentBelowHeader}
      </div>
    </>
  ));

  if (onlyRenderMarquee) {
    return (
      <ErrorBoundary>
        {getMarquee()}
        <QuickLinks links={quickLinksSubnav} />
        {children}
      </ErrorBoundary>
    );
  }

  return (
    <>
      <ErrorBoundary>
        {getHeaderLayout()}
      </ErrorBoundary>
      {children}

      {isTelemundoVertical(vertical)
        && !isEmbedProgramGuide
        && isTelemundoProgramGuideVisible(currentPath)
        && (
          <ErrorBoundary>
            <ProgramGuide url="https://embed.telemundo.com/program-guide/index.html" />
          </ErrorBoundary>
        )}

      <ErrorBoundary>
        <ServiceFooter footer={layout.footer} />
      </ErrorBoundary>
    </>
  );
}

const WrappedHeaderAndFooter = connect(mapStateToProps)(HeaderAndFooter);

export { WrappedHeaderAndFooter as HeaderAndFooter };
